import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../services/api';
import type { ConversationGroup } from '../../types';
import { buildDiscussionPayload } from '../../utils/newDiscussionModel';
import { directThreads, isCustomerRole, newThreadTitle, otherParty } from '../../utils/chatScreenModel';

/**
 * FIX-CHAT-THREADS-GROUPS (חוי 11-05) - in a 1:1 chat: every chat with this person (the click on the person
 * opened the latest one), and "שיחה חדשה" that always opens a SEPARATE chat with an optional subject
 * (empty -> the person's name + the date).
 */
export default function DirectThreadsBar({ group, meId, selectedId, onSelect }: {
  group: ConversationGroup;
  meId: string;
  selectedId: string;
  onSelect: (ticketId: string) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const other = otherParty(group, meId);
  if (!other) return null;
  const threads = directThreads(group.tickets);

  const create = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = buildDiscussionPayload({
        openerId: meId,
        title: newThreadTitle(subject, other.name, format(new Date(), 'dd/MM/yyyy')),
        message: '',
        organizationId: isCustomerRole(other.role) ? group.organization_id : '',
        picked: [other.id],
        people: [{ id: other.id, role: other.role, name: other.name, email: '' }],
        kind: 'conversation',
        newThread: true,
      });
      const id = (await api.createTicket(payload)).data?.id ?? null;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations-grouped'] }),
      ]);
      if (!id) { setError(t('chatScreen.openFailed')); return; }
      setSubject('');
      setCreating(false);
      setOpen(false);
      onSelect(id);
    } catch {
      setError(t('chatScreen.openFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1" data-testid="direct-threads">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="inline-flex items-center gap-1 text-xs text-accent-text hover:underline"
          data-testid="direct-threads-toggle"
        >
          {t('chatScreen.threadsWith', { name: other.name, n: threads.length })}
          {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
        </button>
        <button
          type="button"
          onClick={() => { setCreating(true); setOpen(true); }}
          className="btn btn-secondary btn-sm"
          data-testid="new-thread"
        >
          {t('chatScreen.newThread')}
        </button>
      </div>
      {open && (
        <div className="mt-1.5 rounded-lg border border-surface-300/60 bg-surface-0">
          {creating && (
            <form
              className="flex items-center gap-2 p-2 border-b border-surface-200/80"
              onSubmit={(e) => { e.preventDefault(); void create(); }}
            >
              <input
                className="input flex-1 py-1 text-sm"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('chatScreen.newThreadSubject')}
                aria-label={t('chatScreen.newThreadSubject')}
                maxLength={200}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                data-testid="new-thread-subject"
              />
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy} aria-busy={busy || undefined} data-testid="new-thread-create">{t('chatScreen.newThreadCreate')}</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setCreating(false); setSubject(''); }}>{t('chatScreen.newThreadCancel')}</button>
            </form>
          )}
          {error && <p role="alert" className="px-2 py-1 text-xs text-danger-text">{error}</p>}
          <ul aria-label={t('chatScreen.threadsListLabel')} className="max-h-48 overflow-y-auto">
            {threads.map((tk) => (
              <li key={tk.id}>
                <button
                  type="button"
                  onClick={() => { onSelect(tk.id); setOpen(false); }}
                  aria-current={tk.id === selectedId || undefined}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 text-start text-sm ${tk.id === selectedId ? 'bg-primary-50 font-medium' : 'hover:bg-surface-50'}`}
                  data-testid="direct-thread"
                >
                  <span className="truncate">{tk.title}</span>
                  <span className="text-xs text-surface-500 flex-shrink-0">{tk.last_comment_at ? format(new Date(tk.last_comment_at), 'dd/MM/yyyy') : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
